import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { DiagnosisCode, Medication } from '@xmedical/core';
import { AppMatAutocompleteComponent, XmedicalUiModule } from '@xmedical/ui';
import { Observable } from 'rxjs';

// Data the consumer provides when opening the dialog. The diagnosis-code
// search callback comes through here (rather than as an Angular input)
// because MatDialog.open does not bind inputs on the dialog component.
export interface MedicationItemAddCardData {
  searchTerm?: string;
  diagnosisCodeSearch: (query: string) => Observable<DiagnosisCode[]>;
}

// Pure dialog content — collects medication values and returns them via
// dialogRef.close(). The opener (medication-widget) runs the persist with
// MedicationRepositoryService.
@Component({
  selector: 'xcs-medication-item-add-card',
  templateUrl: './medication-item-add-card.component.html',
  styleUrls: ['./medication-item-add-card.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    XmedicalUiModule,
  ],
})
export class MedicationItemAddCardComponent implements OnInit {

  private dialogRef = inject<MatDialogRef<MedicationItemAddCardComponent>>(MatDialogRef);
  private formBuilder = inject(UntypedFormBuilder);
  protected data = inject<MedicationItemAddCardData>(MAT_DIALOG_DATA);

  validate = false;
  form: UntypedFormGroup | null = null;
  diagnosisCode: DiagnosisCode | null = null;
  @ViewChild('diagnosisAutocomplete') diagnosisAutocomplete?: AppMatAutocompleteComponent;

  get hasDiagnosisCode(): boolean {
    return this.diagnosisCode !== null;
  }

  searchDiagnosisCodes$ = (query: string): Observable<DiagnosisCode[]> => {
    return this.data.diagnosisCodeSearch(query);
  };

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      description: [this.data?.searchTerm || '', Validators.required],
      dosage: ['', Validators.required],
      diagnosisCode: ['', Validators.required],
      comment: [''],
      textColour: [''],
    });
  }

  save(): void {
    if (!this.form) return;
    this.validate = true;
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.dialogRef.close(this.form.value as Medication);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  diagnosisCodeSelected(code: DiagnosisCode): void {
    this.diagnosisCode = code;
    this.form?.get('diagnosisCode')?.patchValue(code.code);
    this.form?.updateValueAndValidity();
    this.diagnosisAutocomplete?.cancelSearch();
  }

  clearDiagnosisCode(): void {
    this.diagnosisCode = null;
    this.form?.get('diagnosisCode')?.setValue('');
    this.form?.updateValueAndValidity();
  }
}
